import * as t from 'io-ts';
import * as Either from 'fp-ts/Either';
import * as Json from 'fp-ts/Json';
import { Eq as eqString } from 'fp-ts/string';
import { getEq as getArrayEq } from 'fp-ts/Array';
import { flow, pipe } from 'fp-ts/function';
import Honeybadger from '@honeybadger-io/js';
import * as Option from 'fp-ts/Option';
import { isClientErrorStatus, isResponseError, isServerErrorStatus, UNPROCESSABLE_ENTITY, } from './http';
import { decodeWith, isDecodingError } from './Decoding';
/**
 * @deprecated in favour of Query
 */
export const Decoding = t.interface({
    tag: t.literal('decoding'),
    errors: t.array(t.string),
    value: t.unknown,
});
/**
 * @deprecated in favour of Query
 */
export const Client = t.interface({
    tag: t.literal('client'),
    message: t.string,
    code: t.number,
    payload: t.unknown,
});
/**
 * @deprecated in favour of Query
 */
export const Server = t.interface({
    tag: t.literal('server'),
    message: t.string,
    code: t.number,
    payload: t.unknown,
});
/**
 * @deprecated in favour of Query
 */
export const Unknown = t.interface({
    tag: t.literal('unknown'),
    error: t.unknown,
});
/**
 * @deprecated in favour of Query
 * Error type for async IO operations.
 */
export const ErrorAsync = t.union([Decoding, Client, Server, Unknown]);
/**
 * a helper function to create a decoding error
 *
 * @example
 * import { ErrorAsync } from '@catawiki/runtime-toolkit'
 *
 * const error = ErrorAsync.decoding(2)(['Expecting string but got number'])
 *
 * @deprecated in favour of Query
 */
export const decoding = (value) => (errors) => ({
    tag: 'decoding',
    errors,
    value,
});
/**
 * @deprecated in favour of Query
 */
export const client = (message, code, payload) => ({
    tag: 'client',
    message,
    code,
    payload,
});
/**
 * @deprecated in favour of Query
 */
export const server = (message, code, payload) => ({
    tag: 'server',
    message,
    code,
    payload,
});
/**
 * @deprecated in favour of Query
 */
export const unknown = (error) => ({
    tag: 'unknown',
    error,
});
/**
 * @deprecated in favour of Query
 */
export const isDecoding = (rd) => rd.tag === 'decoding';
/**
 * @deprecated in favour of Query
 */
export const isClient = (rd) => rd.tag === 'client';
/**
 * @deprecated in favour of Query
 */
export const isServer = (rd) => rd.tag === 'server';
/**
 * @deprecated in favour of Query
 */
export const isUnknown = (rd) => rd.tag === 'unknown';
/**
 * Pattern match on an ErrorAsync value.
 *
 * ```ts
 * const reportError: (err: ErrorAsync) => void = match({
 *   decoding: errors => report('decoding error', errors),
 *   // server should be reporting this one
 *   client: (message, code) =>
 *     isDevelopment && console.log('client error', { message, code }),
 *   // server should be reporting this one
 *   server: (message, code) =>
 *     isDevelopment && console.log('server error', { message, code }),
 *   unknown: error => report('unknown error', error),
 * });
 *
 * reportError(decoding(['Expecting number but got "foo"']));
 *
 * reportError(client('Not found', 404, {}));
 *
 * reportError(server('Server error', 500, {}));
 *
 * reportError(unknown(new Error('undefined is not a function')));
 * ```
 *
 * @deprecated in favour of Query
 */
export const match = (m) => (errorAsync) => {
    if (isDecoding(errorAsync)) {
        return m.decoding(errorAsync);
    }
    if (isClient(errorAsync)) {
        return m.client(errorAsync);
    }
    if (isServer(errorAsync)) {
        return m.server(errorAsync);
    }
    return m.unknown(errorAsync);
};
const getUnknownErrorInfo = (e) => {
    if (e instanceof Error) {
        return {
            isError: true,
            message: e.message,
            stack: e.stack,
        };
    }
    if (e instanceof ErrorEvent) {
        return getUnknownErrorInfo(e.error);
    }
    return pipe(e, Json.stringify, Either.match(getUnknownErrorInfo, (message) => ({
        isError: false,
        message,
    })));
};
/**
 * Notifies errors using
 * [Honeybadger](https://docs.honeybadger.io/lib/javascript/guides/reporting-errors.html)'s
 * format.
 *
 * Note that `ErrorAsync.notify` only notifies `ErrorAsync.Decoding` and
 * `ErrorAsync.Unknown` cases, `ErrorAsync.Client` and `ErrorAsync.Server`
 * aren't notified because the server already logs those.
 *
 * If you need to notify server or client errors make sure to only report those
 * cases to avoid duplicated logs sent to Honeybadger.
 *
 * @deprecated in favour of Query
 */
export const notify = (context, error) => match({
    unknown: ({ error: unknownError }) => Honeybadger.notify({
        name: 'ErrorAsync.Unknown',
        message: `Unknown error on: ${context}`,
        context: getUnknownErrorInfo(unknownError),
        fingerprint: context,
    }),
    decoding: ({ errors, value }) => Honeybadger.notify({
        name: 'ErrorAsync.Decoding',
        message: `Failed to decode on: ${context}`,
        context: { errors, value },
        fingerprint: context,
    }),
    client: () => true,
    server: () => true,
})(error);
/**
 * @deprecated in favour of Query
 */
export const isOfStatus = (status) => match({
    unknown: () => false,
    decoding: () => false,
    client: ({ code }) => code === status,
    server: ({ code }) => code === status,
});
/**
 * [fp-ts Eq](https://gcanti.github.io/fp-ts/modules/Eq.ts.html) instance.
 *
 * @example
 * import { ErrorAsync } from '@catawiki/runtime-toolkit'
 *
 * const E = ErrorAsync.eqErrorAsync
 *
 * assert.strictEqual(E.equals(ErrorAsync.client('Not found', 404, {}), ErrorAsync.client('Not found', 404, {})), true)
 * assert.strictEqual(E.equals(ErrorAsync.client('Bad Request', 400, {}), ErrorAsync.client('Not found', 404, {})), false)
 * // Why would you have 400 as server?
 * assert.strictEqual(E.equals(ErrorAsync.client('Bad Request', 400, {}), ErrorAsync.server('Bad Request', 400, {})), false)
 * assert.strictEqual(E.equals(ErrorAsync.server('Internal Server Error', 500, {}), ErrorAsync.client('Bad Gateway', 502, {})), false)
 * assert.strictEqual(E.equals(ErrorAsync.decoding(null)(['missing x']), ErrorAsync.decoding(null)(['missing x'])), true)
 * assert.strictEqual(E.equals(ErrorAsync.decoding(null)(['missing x']), ErrorAsync.decoding(null)(['y is not here'])), false)
 * assert.strictEqual(E.equals(ErrorAsync.unknown(new Error('error')), ErrorAsync.unknown(new Error('error'))), false)
 *
 * const error = new Error('error')
 * // unknown errors are compared by strict equality
 * assert.strictEqual(E.equals(ErrorAsync.unknown(error), ErrorAsync.unknown(error)), true)
 *
 * @deprecated in favour of Query
 */
export const eqErrorAsync = {
    equals: (x, y) => {
        if (x === y) {
            return true;
        }
        if (isServer(x) && isServer(y)) {
            return x.code === y.code && x.message === y.message;
        }
        if (isClient(x) && isClient(y)) {
            return x.code === y.code && x.message === y.message;
        }
        if (isDecoding(x) && isDecoding(y)) {
            return getArrayEq(eqString).equals(x.errors, y.errors); // ignore the value
        }
        if (isUnknown(x) && isUnknown(y)) {
            return x.error === y.error;
        }
        return false;
    },
};
/**
 * Transforms an [HttpResponseException](https://github.com/catawiki/cw-fe/blob/master/src/http/exceptions/HttpResponseException.ts) error into ErrorAsync.
 *
 * @deprecated in favour of Query
 */
export const httpErrorToErrorAsync = (error) => {
    // Request errors: in this case from `HttpClient`
    // We should be here only for 400's and 500's
    if (isResponseError(error)) {
        const { message, status, payload } = error;
        if (isClientErrorStatus(status)) {
            return client(message, status, payload);
        }
        if (isServerErrorStatus(status)) {
            return server(message, status, payload);
        }
    }
    if (isDecodingError(error)) {
        return decoding(error.payload)(error.errors);
    }
    return unknown(error);
};
/**
 * Transform a promise's error into a `Promise<Either<ErrorAsync, never>>`
 * (ie. `PromiseEither<never>`)
 *
 * @example
 * import { left } from 'fp-ts/Either'
 * import { ErrorAsync } from '@catawiki/runtime-toolkit'
 *
 * const error = new Error('some API error')
 *
 * Promise.reject(error)
 *   .catch(ErrorAsync.errorToLeft)
 *   .then(res => {
 *     assert.deepEqual(res, left(ErrorAsync.unknown(error)))
 *   })
 *
 * @deprecated in favour of Query
 */
export const errorToLeft = flow(httpErrorToErrorAsync, Either.left, (err) => Promise.resolve(err));
/**
 * @deprecated in favour of Query
 */
export const isValidationError = (error) => isOfStatus(UNPROCESSABLE_ENTITY)(error);
/**
 * Decodes payload to the particular type(by default ApiValidationError).
 * Payload is returned along with 422 client or some server errors
 *
 * @deprecated in favour of Query
 */
export const decodeValidationError = (decoder, context) => flow(Option.fromPredicate(isValidationError), Option.map(({ payload }) => decodeWith(decoder)(payload)), Option.chain(Either.matchW((error) => {
    notify(context, error);
    return Option.none;
}, Option.fromNullable)));
